import {
  ACCESS_HELP,
  BulkBar,
  COLUMNS,
  LeaveGuard,
  RoleCells,
  RoleHeadCells,
  RowTools,
  SaveBar,
  rolesKey,
  toggleIn,
  useRolesDraft,
} from "./AccessGrid";
import { Link, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import {
  requestUpdateUser,
  requestUpdateUserContracts,
} from "../services/backendRequests";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "reactstrap";
import { EmptyState } from "../components/EmptyState";
import { FilterBar } from "../clientDashboard/components/FilterBar";
import { HelpTooltip } from "../analyticsDashboard/components/HelpTooltip/HelpTooltip";
import { InlineSpinner } from "../components/InlineSpinner";
import { LevelHeader } from "../clientDashboard/components/LevelHeader";
import LoadingImg from "../components/loading";
import UserDrawer from "./UserDrawer";
import { addDangerAlert } from "../actions/alerts";
import { extendUserWithRole } from "../services/roles";
import { fetchCompanies } from "../actions/companies";
import { useFetch } from "../hooks/useFetch";

// An id only answers a query that looks like one. Support pastes a whole uuid or its
// head, so the match is on the start and needs the eight characters of a uuid's first
// segment: typing "1" then belongs to the names alone, and never drags in every campaign
// whose uuid happens to hold a 1.
const ID_SEARCH_MIN = 8;

const matchesId = (value, needle) =>
  needle.length >= ID_SEARCH_MIN && value.toLowerCase().startsWith(needle);

// Campaign access of one user: one row per campaign, one column per role. A granted role
// is a green dot, a role that is not granted a grey cross. The cell says yes or no and
// nothing else, the name of the role lives in the sticky header above it. So every role
// stays one click away, with no panel to open first.
export const UserPage = () => {
  const { id } = useParams();
  const userId = Number(id);
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);
  const companies = useSelector((state) => state.companies);

  const [user, { refetch: refetchUser, error: userError }] = useFetch(
    `/api/users/${id}`
  );
  const [assigned, { refetch: refetchAssigned, error: assignedError }] =
    useFetch(`/api/users/${id}/contract_infos`);
  const [campaigns, { loading: loadingCampaigns, error: campaignsError }] =
    useFetch("/api/contract_infos?with_site_ids=true");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  // `null` until the filter is touched, so it opens on a client's own company, see
  // `companyFilter`
  const [pickedCompanies, setCompanyFilter] = useState(null);
  // On by default: the page opens on what this user already holds. Searching unticks it,
  // see `onSearch`.
  const [assignedOnly, setAssignedOnly] = useState(true);
  // Holds the campaigns waiting to be saved on screen, whatever company they belong to.
  // Without it, dropping the company filter leaves the save bar counting modifications
  // above an empty list, with no way back to them.
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [selected, setSelected] = useState([]);
  // The company band just added to the filter, which has to be found again wherever the
  // alphabet puts it. Clicking a cell blinks nothing: the eye is already on the row.
  const [flash, setFlash] = useState([]);

  // The company list feeds the edit drawer, which can be opened before the users page
  // ever loaded it (this page is reachable by URL).
  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  const initialRoles = useMemo(
    () =>
      Object.fromEntries(
        (assigned ?? []).map((campaign) => [
          campaign.id,
          campaign.users.find((member) => member.id === userId)?.roles ?? [],
        ])
      ),
    [assigned, userId]
  );

  const { roles, apply, setRoles, reset } = useRolesDraft(initialRoles);

  // What the server holds, as this page knows it: the assignments it read, or what the
  // last save wrote while the refetch is still out. `list_by_user` serializes the sites,
  // members and applications of every campaign, so that round trip takes seconds, and
  // without this the save bar would sit there the whole time counting changes the server
  // already has.
  const [savedRoles, setSavedRoles] = useState(null);
  const baseline = savedRoles ?? initialRoles;

  useEffect(() => setSavedRoles(null), [assigned]);

  // Brings the new band to the top of the box, not merely into view: what matters is the
  // campaigns listed under it. Then lets the blink run out.
  useEffect(() => {
    if (flash.length === 0) return undefined;

    document
      .querySelector(`[data-flash="${flash[0]}"]`)
      ?.scrollIntoView({ block: "start", behavior: "smooth" });

    const timer = setTimeout(() => setFlash([]), 1200);
    return () => clearTimeout(timer);
  }, [flash]);

  // A company added to the filter is what the eye is looking for, wherever the alphabet
  // puts its band in the list. Removing one flashes nothing: there is no new block to
  // find, and the list stays where it is.
  const pickCompanies = (next) => {
    setCompanyFilter(next);
    setFlash(next.filter((companyId) => !companyFilter.includes(companyId)));
  };

  const changed = [
    ...new Set([...Object.keys(baseline), ...Object.keys(roles)]),
  ].filter(
    (campaignId) =>
      rolesKey(baseline[campaignId]) !== rolesKey(roles[campaignId])
  );

  const save = async () => {
    setSaving(true);
    try {
      await requestUpdateUserContracts(
        Object.entries(roles)
          .filter(
            ([campaignId, list]) =>
              list.length > 0 ||
              // An assignment that already carried no role and was not touched here has
              // to be sent back as is: the PUT replaces the whole list, so leaving it out
              // would unassign the user from a campaign nobody asked to change.
              (baseline[campaignId]?.length === 0 &&
                !changed.includes(campaignId))
          )
          .map(([campaignId, list]) => ({ id: campaignId, roles: list })),
        userId,
        currentUser
      );
      // The draft stays, and becomes the reference: it is exactly what the server now
      // holds, so the save bar leaves at once instead of waiting for the refetch, and a
      // change made during that wait is still counted against the saved state.
      setSavedRoles(roles);
      refetchAssigned();
      // A selection that outlives its save is a trap: presets replace, so one click
      // would rewrite the same twenty campaigns.
      setSelected([]);
    } catch (error) {
      dispatch(addDangerAlert(error));
    }
    setSaving(false);
  };

  const onSubmitUser = async (form) => {
    try {
      await requestUpdateUser(form.id, form, currentUser);
      setEditing(false);
      refetchUser();
    } catch (error) {
      dispatch(addDangerAlert(error));
    }
  };

  const ownCompanyId = user?.company?.id;
  // The serializer ships `admin` and `backoffice`, never the role itself, so it is
  // derived here by the same helper the users list uses.
  const role = user && extendUserWithRole(user).role;
  // Staff access spans client companies, their own is not where they are looked up
  const isStaff = Boolean(user?.backoffice);

  const companyOptions = [
    ...new Map(
      (campaigns ?? []).map(({ company }) => [company.id, company])
    ).values(),
  ]
    .map(({ id: companyId, name }) => ({
      value: companyId,
      label: name,
      divider: !isStaff && companyId === ownCompanyId,
    }))
    // A client's own company leads, split from the others by a line
    .sort((a, b) => b.divider - a.divider || a.label.localeCompare(b.label));

  // Preselected only when it owns a campaign: otherwise it is not an option, the select
  // would read "All companies" while filtering everything out. Never for staff, see
  // `isStaff`.
  const companyFilter =
    pickedCompanies ??
    (companyOptions.some(({ divider }) => divider) ? [ownCompanyId] : []);

  // Several hundred campaigns live on the platform, so nothing is listed until one of
  // the three filters narrows it down. They then combine: ticking "assigned only" on
  // top of a company shows what this user holds inside that company, not everything
  // he holds.
  // A filter with nothing left to filter is a dead end: its checkbox is only offered
  // while something is modified, so an undo would hide the box and leave the filter on,
  // matching nothing and impossible to untick short of reloading the page. Ignored here,
  // then dropped by the effect, or it would come back on its own with the next change.
  const onlyModified = modifiedOnly && changed.length > 0;

  useEffect(() => {
    if (changed.length === 0) setModifiedOnly(false);
  }, [changed.length]);

  // The filters are AND-ed, so a search under "Assigned only" could only ever return
  // what is already assigned. Typing unticks the box rather than silently ignoring it,
  // so the filters on screen always describe the list below.
  const onSearch = (value) => {
    setSearch(value);
    if (value) setAssignedOnly(false);
  };

  const needle = search.trim().toLowerCase();

  // The page as it opens on a user who holds nothing: the default filters alone, nothing
  // typed or picked.
  const onlyAssigned =
    assignedOnly &&
    pickedCompanies === null &&
    needle === "" &&
    Object.keys(baseline).length === 0;

  const narrowed =
    assignedOnly || onlyModified || companyFilter.length > 0 || needle !== "";

  const visible = narrowed
    ? (campaigns ?? []).filter(
        ({ id: campaignId, name, company, site_ids: siteIds }) =>
          // The UUIDs are searchable too: a campaign is often chased from a log line
          // or a support ticket, which carry the id and not the name. A site id finds
          // its campaign, where the access to it is granted.
          (!needle ||
            name.toLowerCase().includes(needle) ||
            company.name.toLowerCase().includes(needle) ||
            matchesId(campaignId, needle) ||
            matchesId(company.id, needle) ||
            (siteIds ?? []).some((siteId) => matchesId(siteId, needle))) &&
          (companyFilter.length === 0 || companyFilter.includes(company.id)) &&
          // Assigned means a membership, not a granted role, hence the key and not the
          // length: a campaign held with no role at all would otherwise read as none.
          // And on the saved state, never the draft, or a row would leave the list the
          // instant its last access is removed.
          (!assignedOnly || campaignId in baseline) &&
          (!onlyModified || changed.includes(campaignId))
      )
    : [];

  // The save bar reaches what it is about to write from anywhere: the other filters are
  // dropped, so a campaign modified under a company that is no longer selected comes
  // back on screen. Leaving the filter is done from the filter bar, where it shows.
  const reviewChanges = () => {
    setSearch("");
    setCompanyFilter([]);
    setAssignedOnly(false);
    setModifiedOnly(true);
  };

  const groups = [
    ...visible
      .reduce((byCompany, campaign) => {
        const group = byCompany.get(campaign.company.id) ?? {
          company: campaign.company,
          campaigns: [],
        };
        group.campaigns.push(campaign);
        return byCompany.set(campaign.company.id, group);
      }, new Map())
      .values(),
  ].sort((a, b) => a.company.name.localeCompare(b.company.name));

  const visibleIds = visible.map(({ id: campaignId }) => campaignId);

  // A campaign that left the list keeps no hold on the bulk bar: filtering it out then
  // hitting "Basic" would grant an access nobody has on screen.
  const selectedVisible = selected.filter((campaignId) =>
    visibleIds.includes(campaignId)
  );

  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const renderGroup = ({ company, campaigns: companyCampaigns }) => {
    const ids = companyCampaigns.map(({ id: campaignId }) => campaignId);
    const allSelected = ids.every((campaignId) =>
      selected.includes(campaignId)
    );

    return (
      <React.Fragment key={company.id}>
        <tr
          className={
            "user-access__grouprow" +
            (flash.includes(company.id) ? " is-new" : "")
          }
          data-flash={company.id}
        >
          <td className="user-access__check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={allSelected}
              title={`Select the campaigns of ${company.name}`}
              onChange={() =>
                setSelected(
                  allSelected
                    ? selected.filter((campaignId) => !ids.includes(campaignId))
                    : [...new Set([...selected, ...ids])]
                )
              }
            />
          </td>
          <td colSpan={COLUMNS - 1}>
            <span className="user-access__groupname">{company.name}</span>
            <span
              className="user-access__groupcount"
              title="Campaigns of this company where this user holds at least one access"
            >
              {
                ids.filter((campaignId) => (roles[campaignId] ?? []).length > 0)
                  .length
              }{" "}
              / {ids.length}
            </span>
          </td>
        </tr>

        {companyCampaigns.map((campaign) => {
          const list = roles[campaign.id] ?? [];

          return (
            <tr key={campaign.id}>
              <td className="user-access__check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selected.includes(campaign.id)}
                  aria-label={`Select ${campaign.name}`}
                  onChange={() => setSelected(toggleIn(selected, campaign.id))}
                />
              </td>

              <td className="user-access__campaign">
                <Link
                  to={`/campaigns/${campaign.id}`}
                  className="user-access__open"
                  title={`Open ${campaign.name}`}
                  aria-label={`Open ${campaign.name}`}
                >
                  <i className="fa-solid fa-file-signature" />
                </Link>
                {campaign.name}
                {!campaign.active && (
                  <span className="user-access__inactive">inactive</span>
                )}
              </td>

              <RowTools
                name={campaign.name}
                onPreset={(roleValues) => setRoles([campaign.id], roleValues)}
                unassign={
                  list.length > 0 && {
                    label: "Unassign",
                    title: `Remove ${campaign.name} from this user`,
                    onClick: () => setRoles([campaign.id], []),
                  }
                }
              />

              <RoleCells
                list={list}
                name={campaign.name}
                onToggle={(role, granted) =>
                  apply([campaign.id], [role], granted)
                }
              />
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  // Only while there is nothing to show. `useFetch` keeps what it already read, so a
  // refetch that fails after a successful save would otherwise replace a working grid,
  // and the draft still held in it, with a page saying the user does not exist.
  if ((userError && !user) || (assignedError && !assigned))
    return (
      <div className="page-content container-fluid cf-page">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="User not available"
          text="This user could not be loaded. Check the address, or try again."
        />
      </div>
    );

  // Nothing is drawn before the assigned campaigns are known. On an empty `initialRoles`
  // a single click freezes a draft that ignores everything this user already holds, and
  // `update_user_contracts` replaces the whole list, so the save would wipe it.
  if (!user || !assigned)
    return (
      <div className="page-content container-fluid cf-page">
        <LevelHeader
          path={[{ label: "Users", to: "/users" }, { label: "User" }]}
        />
        <LoadingImg visible />
      </div>
    );

  return (
    <>
      <LeaveGuard when={changed.length > 0} />

      <UserDrawer
        alert=""
        user={user}
        companies={companies}
        isOpen={editing}
        onClose={() => setEditing(false)}
        onSubmit={onSubmitUser}
      />

      <div className="page-content container-fluid cf-page">
        <LevelHeader
          path={[{ label: "Users", to: "/users" }, { label: user.fullname }]}
          metrics={[{ value: role, label: "Role" }]}
          actions={
            // Same header action as the campaign page: outlined primary, labelled
            // next to its pencil rather than a bare icon.
            <Button
              color="primary"
              outline
              className="text-nowrap"
              onClick={() => setEditing(true)}
            >
              <i className="fa-solid fa-pencil me-1" aria-hidden="true" /> Edit
              information
            </Button>
          }
        />

        <FilterBar
          query={search}
          onQuery={onSearch}
          placeholder="Search a campaign, a company, an id..."
          selects={[
            {
              value: companyFilter,
              onChange: pickCompanies,
              allLabel: "All companies",
              options: companyOptions,
            },
          ]}
          extraActive={assignedOnly || modifiedOnly}
          extra={
            <>
              <label
                className={
                  "cf-checkfilter" + (assignedOnly ? " is-active" : "")
                }
              >
                <input
                  type="checkbox"
                  checked={assignedOnly}
                  onChange={(event) => setAssignedOnly(event.target.checked)}
                />
                Assigned only
              </label>

              {/* Only while something is waiting to be saved: the rest of the time it
                  would filter on an empty set. */}
              {changed.length > 0 && (
                <label
                  className={
                    "cf-checkfilter" + (modifiedOnly ? " is-active" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={modifiedOnly}
                    onChange={(event) => setModifiedOnly(event.target.checked)}
                  />
                  Modified only
                </label>
              )}
            </>
          }
          // The question mark lives here and not in the table head: the table is inside
          // a scrolling box, and a popover anchored in there gets clipped.
          trailing={
            <HelpTooltip helpTooltip={ACCESS_HELP} placement="bottom-end" />
          }
        />

        {selectedVisible.length > 0 && (
          <BulkBar
            count={selectedVisible.length}
            noun="campaign"
            onPreset={(roleValues) => setRoles(selectedVisible, roleValues)}
            onAddRole={(role) => apply(selectedVisible, [role], true)}
            unassign={{
              label: "Unassign",
              title: "Remove the selected campaigns from this user",
              onClick: () => setRoles(selectedVisible, []),
            }}
            onDeselect={() => setSelected([])}
          />
        )}

        {/* Same scroll model as the Users and Admin tables: the rows scroll here,
            under a header and filters that stay put, so the page never gets a
            second scrollbar. Both axes scroll in this one box, otherwise a nested
            horizontal scroller would become the containing block of the sticky head
            and the head would never pin. */}
        <div className="user-access-scroll">
          {loadingCampaigns && <InlineSpinner text="Loading campaigns..." />}

          {!campaignsError && !loadingCampaigns && !narrowed && (
            <EmptyState
              icon="fa-solid fa-filter"
              title="Pick a company"
              text="Filter by company, tick Assigned only, or search a campaign."
            />
          )}

          {campaignsError && (
            <EmptyState
              icon="fa-solid fa-triangle-exclamation"
              title="Campaigns not available"
              text="The campaign list could not be loaded. Reload the page to try again."
            />
          )}

          {!campaignsError &&
            !loadingCampaigns &&
            narrowed &&
            visible.length === 0 && (
              // Nothing was searched on a brand new user, the default filter is simply
              // showing an empty set, and "no campaign matches your search" would be a
              // lie on the very case this page exists for.
              <EmptyState
                icon="fa-solid fa-file-signature"
                title={onlyAssigned ? "No campaign assigned" : "No campaign"}
                text={
                  onlyAssigned
                    ? "This user holds no campaign yet. Untick Assigned only, or search one, to grant the first."
                    : "No campaign matches your search."
                }
              />
            )}

          {visible.length > 0 && (
            <div className="user-access-wrap">
              <table className="user-access">
                <thead>
                  <tr>
                    {/* Acts on what the filters leave on screen, like the company
                        boxes below it: a campaign that is not listed is never picked
                        up by a bulk action. */}
                    <th className="user-access__check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allVisibleSelected}
                        title="Select every campaign listed"
                        onChange={() =>
                          setSelected(
                            allVisibleSelected
                              ? selected.filter(
                                  (campaignId) =>
                                    !visibleIds.includes(campaignId)
                                )
                              : [...new Set([...selected, ...visibleIds])]
                          )
                        }
                      />
                    </th>
                    <th>Campaign</th>
                    <th />
                    <RoleHeadCells />
                  </tr>
                </thead>
                <tbody>{groups.map(renderGroup)}</tbody>
              </table>
            </div>
          )}
        </div>

        {changed.length > 0 && (
          <SaveBar
            count={changed.length}
            noun="campaign"
            onReview={!modifiedOnly && reviewChanges}
            onCancel={reset}
            onSave={save}
            saving={saving}
          />
        )}
      </div>
    </>
  );
};

export default UserPage;
